// ============================================================================
// Delhivery booking modal — the full "Book a Delhivery" flow for one PO, launched from an eligible
// row of the OMS PO list (replacing the standalone tab). Two popups:
//   1. Booking — auto-checks the PO on open: an already-booked PO shows its booking + a "Refresh live
//      status" button (and a Rebook prompt when the status says can_rebook); a free PO shows the
//      vehicle picker + editable contacts + Book.
//   2. Live status — opened by "Refresh live status", shows the merged DB + Delhivery Track state.
// Self-contained; talks only to the delhivery-* API functions in ../../../apis/po.api.
// ============================================================================
import {
  Alert,
  Button,
  Descriptions,
  Modal,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { CarOutlined } from "@ant-design/icons";
import React from "react";
import {
  DelhiveryResult,
  delhiveryBookOrder,
  delhiveryOrderStatus,
  delhiveryQuote,
} from "../../../apis/po.api";
import ContactSelect, { defaultContactValue } from "./contact_select";

const { Text } = Typography;

// One selectable vehicle mode from the quote's `vehicles[]`. Fields beyond these vary, so it stays loose.
interface QuoteVehicle {
  type: string;
  fare?: { currency?: string; amount?: number };
  capacity?: { value?: number; unit?: string };
  eligible?: boolean;
  priority?: number;
  ineligible_reason?: string | null;
  max_dimensions?: string | null; // display string only (units vary); render verbatim, may be null
}

// One line of the quote's `items[]` / status's `po_items[]` preview.
interface QuoteItem {
  po_item_id: number;
  name: string;
  quantity: number;
  category?: string;
  included: boolean;
  weight_kg?: number | null;
  volume_cm3?: number | null;
  max_length_ft?: number | null;
  reason?: string;
  rejection_reason?: string | null; // why this item blocks the booking (shown when not bookable)
}

// When a quote can't be booked, the cargo carries a specific rejection_reason — cargo-level, or on
// the offending items — which is far more useful than the generic "not eligible / no vehicle for
// this cargo". Prefer it; the caller falls back to the backend error, then a hardcoded default.
export const cargoRejectionReason = (data: any): string | undefined => {
  if (!data) return undefined;
  if (data.rejection_reason) return data.rejection_reason;
  const perItem: string[] = (data.items ?? [])
    .map((it: any) => it?.rejection_reason)
    .filter(Boolean);
  const unique = Array.from(new Set(perItem));
  return unique.length ? unique.join("; ") : undefined;
};

// Local vehicle images (public/images/), keyed by the quote's `type`; unmapped types fall back to an icon.
const VEHICLE_IMAGES: Record<string, string> = {
  "2-wheeler": "/images/2_wheeler.webp",
  "mini-3w": "/images/mini_3w.webp",
  "3-wheeler": "/images/3_wheeler.webp",
  "tata-ace": "/images/tata_ace.webp",
  "8ft-pickup": "/images/8_ft.webp",
};

const VehicleThumb: React.FC<{ vehicle: QuoteVehicle }> = ({ vehicle }) => {
  const [errored, setErrored] = React.useState(false);
  const src = VEHICLE_IMAGES[vehicle.type];
  if (src && !errored) {
    return (
      <img
        src={src}
        alt={vehicle.type}
        onError={() => setErrored(true)}
        style={{ width: 44, height: 44, objectFit: "contain", borderRadius: 4 }}
      />
    );
  }
  return (
    <CarOutlined
      style={{ fontSize: 28, color: "#8c8c8c", width: 44, textAlign: "center" }}
      aria-label={vehicle.type}
    />
  );
};

const rupee = (v: unknown) =>
  v === null || v === undefined || v === "" ? "—" : `₹${v}`;

// partner_info.mobile is { countryCode, mobileNumber } — join them, or "—" when not yet surfaced.
const formatMobile = (mobile: any): string => {
  const num = mobile?.mobileNumber?.trim();
  if (!num) return "—";
  const cc = mobile?.countryCode ? `+${mobile.countryCode} ` : "";
  return `${cc}${num}`;
};

const TrackingLink: React.FC<{ url?: string | null }> = ({ url }) =>
  url ? (
    <a href={url} target="_blank" rel="noopener noreferrer">
      Track on Delhivery ↗
    </a>
  ) : (
    <Text type="secondary">—</Text>
  );

// The PO items preview — shared by the quote (items) and status (po_items) views.
const ItemsPreview: React.FC<{ items?: QuoteItem[]; label?: string }> = ({
  items,
  label = "Items",
}) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div style={{ marginTop: 16 }}>
      <Text strong>
        {label} ({items.length})
      </Text>
      <Table<QuoteItem>
        style={{ marginTop: 8 }}
        size="small"
        pagination={false}
        rowKey="po_item_id"
        dataSource={items}
        scroll={{ x: "max-content" }}
        rowClassName={(it) => (it.included ? "" : "delhivery-item-excluded")}
        columns={[
          {
            title: "Item",
            dataIndex: "name",
            render: (name: string, it) => (
              <Space size={6} align="center" wrap>
                <span style={{ color: it.included ? undefined : "#999" }}>
                  {name}
                </span>
                {it.included ? (
                  <Tag color="green" style={{ marginInlineEnd: 0 }}>
                    shipped
                  </Tag>
                ) : (
                  <Tooltip title={it.reason || undefined}>
                    <Tag style={{ marginInlineEnd: 0 }}>not shipped</Tag>
                  </Tooltip>
                )}
              </Space>
            ),
          },
          { title: "Qty", dataIndex: "quantity", width: 64 },
          { title: "Category", dataIndex: "category", render: (c) => c || "—" },
          {
            title: "Weight",
            dataIndex: "weight_kg",
            width: 110,
            render: (w: number | null | undefined, it) =>
              !it.included ? (
                "—"
              ) : w == null ? (
                <Tooltip title="No density mapping for this category — contributed 0 kg">
                  <Text type="warning">no density</Text>
                </Tooltip>
              ) : (
                `${w} kg`
              ),
          },
          {
            title: "Volume",
            dataIndex: "volume_cm3",
            width: 120,
            render: (v: number | null | undefined, it) =>
              !it.included || v == null ? "—" : `${v} cm³`,
          },
          {
            title: "Max length",
            dataIndex: "max_length_ft",
            width: 110,
            render: (l: number | null | undefined, it) =>
              !it.included || l == null ? "—" : `${l} ft`,
          },
        ]}
      />
    </div>
  );
};

// The live-status body (popup 2). `data` is the status endpoint's response.
const LiveStatusBody: React.FC<{ data: any }> = ({ data }) => (
  <>
    <Descriptions size="small" column={1} bordered>
      <Descriptions.Item label="OMS status">
        <Tag color="blue">{data.fulfilment_status ?? "—"}</Tag>
      </Descriptions.Item>
      {(data.pickup_person_name || data.pickup_contact_number) && (
        <Descriptions.Item label="Pickup contact">
          {[data.pickup_person_name, data.pickup_contact_number]
            .filter(Boolean)
            .join(" · ") || "—"}
        </Descriptions.Item>
      )}
      {(data.drop_person_name || data.drop_contact_number) && (
        <Descriptions.Item label="Drop contact">
          {[data.drop_person_name, data.drop_contact_number]
            .filter(Boolean)
            .join(" · ") || "—"}
        </Descriptions.Item>
      )}
      {data.partner_info && (
        <>
          <Descriptions.Item label="Rider">
            {data.partner_info.name ?? data.delivery_person_name ?? "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Rider phone">
            {formatMobile(data.partner_info.mobile)}
          </Descriptions.Item>
          <Descriptions.Item label="Vehicle plate">
            {data.partner_info.vehicleNumber ?? data.vehicle_number ?? "—"}
          </Descriptions.Item>
          {data.partner_info.vehicleType && (
            <Descriptions.Item label="Vehicle type">
              {data.partner_info.vehicleType}
            </Descriptions.Item>
          )}
          {data.partner_info.location && (
            <Descriptions.Item label="Rider location">
              {data.partner_info.location.lat}, {data.partner_info.location.long}
            </Descriptions.Item>
          )}
        </>
      )}
      {data.partner_map_url && (
        <Descriptions.Item label="Live map">
          <a href={data.partner_map_url} target="_blank" rel="noopener noreferrer">
            Open rider location in Google Maps ↗
          </a>
        </Descriptions.Item>
      )}
      <Descriptions.Item label="Fare">
        {rupee(
          data.fare_details?.estimatedFareDetails?.amount ?? data.fare
        )}
      </Descriptions.Item>
      <Descriptions.Item label="Tracking">
        <TrackingLink url={data.tracking_url} />
      </Descriptions.Item>
    </Descriptions>
    <ItemsPreview items={data.po_items} label="PO items" />
  </>
);

export const DelhiveryBookingModal: React.FC<{
  poNumber: string;
  open: boolean;
  onClose: () => void;
}> = ({ poNumber, open, onClose }) => {
  const [quote, setQuote] = React.useState<DelhiveryResult | null>(null);
  const [booking, setBooking] = React.useState<any | null>(null);
  const [checking, setChecking] = React.useState(false);
  const [booking_, setBooking_] = React.useState(false);
  const [selectedVehicle, setSelectedVehicle] = React.useState<string | undefined>();
  // Per-handover OTP flags — Delhivery sends a code to that end's contact phone. Both default OFF.
  const [dropOtpEnabled, setDropOtpEnabled] = React.useState(false);
  const [pickupOtpEnabled, setPickupOtpEnabled] = React.useState(false);
  const [pickupContact, setPickupContact] = React.useState("");
  const [dropContact, setDropContact] = React.useState("");
  const [canRebook, setCanRebook] = React.useState(false);

  // Popup 2 (live status).
  const [statusOpen, setStatusOpen] = React.useState(false);
  const [status, setStatus] = React.useState<DelhiveryResult | null>(null);
  const [statusLoading, setStatusLoading] = React.useState(false);

  const resetAll = () => {
    setQuote(null);
    setBooking(null);
    setSelectedVehicle(undefined);
    setDropOtpEnabled(false);
    setPickupOtpEnabled(false);
    setPickupContact("");
    setDropContact("");
    setCanRebook(false);
    setStatus(null);
    setStatusOpen(false);
  };

  // Run the quote for this PO and set up the vehicle-selection state.
  const runQuoteFlow = React.useCallback(async () => {
    setChecking(true);
    const res = await delhiveryQuote(poNumber);
    setQuote(res);
    if (res.status === 200) {
      // Default both ends to the assigned ops owner's number; fall back to the quote's own defaults.
      const assigned = res.data?.assigned_to_contact || "";
      setPickupContact(
        assigned ||
          defaultContactValue(res.data?.pickup_contact_options, res.data?.pickup_contact_number)
      );
      setDropContact(
        assigned ||
          defaultContactValue(res.data?.drop_contact_options, res.data?.drop_contact_number)
      );
    }
    setChecking(false);
  }, [poNumber]);

  // On open: status first (already-booked POs show their booking, not a quote); 404 → quote.
  React.useEffect(() => {
    if (!open) return;
    resetAll();
    let cancelled = false;
    (async () => {
      setChecking(true);
      const existing = await delhiveryOrderStatus(poNumber);
      if (cancelled) return;
      if (existing.ok && existing.data) {
        setBooking(existing.data);
        setCanRebook(existing.data.can_rebook === true);
        setChecking(false);
        return;
      }
      const res = await delhiveryQuote(poNumber);
      if (cancelled) return;
      setQuote(res);
      if (res.status === 200) {
        // Default both ends to the assigned ops owner's number; fall back to the quote's defaults.
        const assigned = res.data?.assigned_to_contact || "";
        setPickupContact(
          assigned ||
            defaultContactValue(res.data?.pickup_contact_options, res.data?.pickup_contact_number)
        );
        setDropContact(
          assigned ||
            defaultContactValue(res.data?.drop_contact_options, res.data?.drop_contact_number)
        );
      }
      setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, poNumber]);

  const runBook = async () => {
    setBooking_(true);
    const res = await delhiveryBookOrder(poNumber, {
      vehicle_mode: selectedVehicle,
      pickup_contact_number: pickupContact.trim() || undefined,
      drop_contact_number: dropContact.trim() || undefined,
      pickup_otp_enabled: pickupOtpEnabled,
      drop_otp_enabled: dropOtpEnabled,
    });
    setBooking_(false);
    if (res.ok) {
      setBooking(res.data);
      setQuote(null);
      message.success("Delhivery order booked");
    } else if (res.status === 409) {
      setBooking(res.data);
      setQuote(null);
      message.info("This PO is already booked with Delhivery");
    } else {
      message.error(res.error || "Could not book this PO with Delhivery");
    }
  };

  // Rebook — drop the cancelled booking and run a fresh quote (manual-only: nothing books till Book).
  const runRebook = async () => {
    setBooking(null);
    setCanRebook(false);
    setSelectedVehicle(undefined);
    await runQuoteFlow();
  };

  const openLiveStatus = async () => {
    setStatusOpen(true);
    setStatusLoading(true);
    const res = await delhiveryOrderStatus(poNumber);
    setStatus(res);
    setStatusLoading(false);
    if (res.status === 404) message.info("No Delhivery booking on this PO");
    else if (!res.ok) message.error(res.error || "Could not fetch status");
  };

  // Derived vehicle-selection state.
  const eligible = quote?.status === 200;
  const vehicles: QuoteVehicle[] = [...(quote?.data?.vehicles ?? [])].sort(
    (a, b) => (a.priority ?? 99) - (b.priority ?? 99)
  );
  const recommended =
    vehicles.find((v) => v.priority === 1) ??
    vehicles.find((v) => v.eligible !== false);
  const chosen = vehicles.find((v) => v.type === selectedVehicle) ?? recommended;
  const bookLabel =
    eligible && chosen
      ? `Book Delhivery · ${chosen.type} · ${rupee(chosen.fare?.amount)}${
          !selectedVehicle ? " (Recommended)" : ""
        }`
      : "Book Delhivery";
  const disabledReason =
    quote && quote.status === 400
      ? cargoRejectionReason(quote.data) || quote.error || "Not eligible for Delhivery"
      : undefined;

  return (
    <>
      <Modal
        title={`Book Delhivery · ${poNumber}`}
        open={open}
        onCancel={onClose}
        footer={null}
        width={640}
        destroyOnClose
      >
        <style>{`
          .delhivery-vehicle-row:not([aria-disabled="true"]):hover {
            border-color: #24AAB7 !important;
            box-shadow: 0 2px 10px rgba(36,170,183,0.15);
          }
          .delhivery-item-excluded td { background: #fafafa; }
        `}</style>

        {checking && (
          <div style={{ textAlign: "center", padding: 24 }}>
            <Spin />
          </div>
        )}

        {/* Rebook prompt — strictly from status.can_rebook. */}
        {!checking && booking && canRebook && (
          <Alert
            style={{ marginBottom: 16 }}
            type="warning"
            showIcon
            message="Delivery cancelled"
            description="This PO's delivery was cancelled — the active leg is free to rebook via Delhivery."
            action={
              <Button type="primary" onClick={runRebook}>
                Rebook via Delhivery
              </Button>
            }
          />
        )}

        {/* Already-booked summary + live-status entry (popup 2). */}
        {!checking && booking && (
          <>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="Order ID">
                {booking.order_id ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Vehicle">
                {booking.vehicle_mode ?? booking.partner_info?.vehicleType ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Fare">
                {rupee(
                  booking.fare ??
                    booking.fare_details?.estimatedFareDetails?.amount
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Tracking">
                <TrackingLink url={booking.tracking_url} />
              </Descriptions.Item>
            </Descriptions>
            <Button
              type="primary"
              style={{ marginTop: 16 }}
              loading={statusLoading}
              onClick={openLiveStatus}
            >
              Refresh live status
            </Button>
            <ItemsPreview items={booking.po_items} label="PO items" />
          </>
        )}

        {/* Fresh quote → vehicle picker + contacts + Book. */}
        {!checking && !booking && quote && (
          <div>
            {eligible && vehicles.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                {vehicles.map((v) => {
                  const modeIneligible = v.eligible === false;
                  const selected = v.type === selectedVehicle;
                  return (
                    <div
                      key={v.type}
                      role="button"
                      aria-disabled={modeIneligible}
                      aria-pressed={selected}
                      onClick={() =>
                        !modeIneligible &&
                        setSelectedVehicle(selected ? undefined : v.type)
                      }
                      className="delhivery-vehicle-row"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "10px 14px",
                        marginBottom: 10,
                        borderRadius: 10,
                        border: `1.5px solid ${selected ? "#24AAB7" : "#f0f0f0"}`,
                        background: selected ? "#e6fbfc" : "#fff",
                        cursor: modeIneligible ? "not-allowed" : "pointer",
                        opacity: modeIneligible ? 0.55 : 1,
                        transition:
                          "border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease",
                        boxShadow: selected
                          ? "0 1px 6px rgba(36,170,183,0.18)"
                          : "none",
                      }}
                    >
                      <VehicleThumb vehicle={v} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Space wrap align="center" size={8}>
                          <Text strong style={{ textTransform: "capitalize" }}>
                            {v.type}
                          </Text>
                          {v.priority === 1 && (
                            <Tag color="green" style={{ marginInlineEnd: 0 }}>
                              Recommended
                            </Tag>
                          )}
                          {modeIneligible && (
                            <Tooltip title={v.ineligible_reason || undefined}>
                              <Tag color="red" style={{ marginInlineEnd: 0 }}>
                                Unavailable
                              </Tag>
                            </Tooltip>
                          )}
                        </Space>
                        {v.capacity?.value != null && (
                          <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              up to {v.capacity.value} {v.capacity.unit ?? "kg"}
                            </Text>
                          </div>
                        )}
                        {v.max_dimensions && (
                          <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Max size: {v.max_dimensions}
                            </Text>
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <Text strong style={{ fontSize: 15 }}>
                          {rupee(v.fare?.amount)}
                        </Text>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {eligible && (
              <Space wrap size={16} style={{ marginBottom: 16 }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                    Pickup contact
                  </Text>
                  <ContactSelect
                    options={quote?.data?.pickup_contact_options}
                    value={pickupContact}
                    onChange={setPickupContact}
                  />
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                    Drop contact
                  </Text>
                  <ContactSelect
                    options={quote?.data?.drop_contact_options}
                    value={dropContact}
                    onChange={setDropContact}
                  />
                </div>
              </Space>
            )}

            {eligible && (
              <div style={{ marginBottom: 16 }}>
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  <Space align="start" size={10}>
                    <Switch checked={dropOtpEnabled} onChange={setDropOtpEnabled} />
                    <div>
                      <Text strong>OTP at delivery</Text>
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Delhivery sends a code to the customer; the rider must collect it to
                          complete delivery.
                        </Text>
                      </div>
                    </div>
                  </Space>
                  <Space align="start" size={10}>
                    <Switch checked={pickupOtpEnabled} onChange={setPickupOtpEnabled} />
                    <div>
                      <Text strong>OTP at pickup</Text>
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Delhivery sends a code to the pickup contact(s); the rider must collect it
                          to complete pickup.
                        </Text>
                      </div>
                    </div>
                  </Space>
                </Space>
              </div>
            )}

            {eligible && (
              <div>
                <Tooltip title={disabledReason}>
                  <Button
                    type="primary"
                    disabled={!eligible}
                    loading={booking_}
                    onClick={runBook}
                  >
                    {bookLabel}
                  </Button>
                </Tooltip>
                {selectedVehicle && (
                  <Button type="link" onClick={() => setSelectedVehicle(undefined)}>
                    Use recommended
                  </Button>
                )}
              </div>
            )}

            {eligible && <ItemsPreview items={quote?.data?.items} />}

            {quote.status === 400 && (
              <Alert
                type="warning"
                showIcon
                message="Not bookable on Delhivery"
                description={
                  cargoRejectionReason(quote.data) ||
                  quote.error ||
                  "Multi-pickup, multi-leg, not serviceable, or over capacity."
                }
              />
            )}
          </div>
        )}
      </Modal>

      {/* Popup 2 — live status. */}
      <Modal
        title={`Live status · ${poNumber}`}
        open={statusOpen}
        onCancel={() => setStatusOpen(false)}
        footer={
          <Button loading={statusLoading} onClick={openLiveStatus}>
            Refresh
          </Button>
        }
        width={600}
        destroyOnClose
      >
        {statusLoading && (
          <div style={{ textAlign: "center", padding: 24 }}>
            <Spin />
          </div>
        )}
        {!statusLoading && status?.ok && status.data && (
          <LiveStatusBody data={status.data} />
        )}
        {!statusLoading && status?.status === 404 && (
          <Alert type="info" showIcon message="No Delhivery booking on this PO yet" />
        )}
      </Modal>
    </>
  );
};

export default DelhiveryBookingModal;
